import { createEvent, verifyEvent, AuthEvent, EventType, EventPayload } from './events';
import { loadOrCreateIdentity, verifySignature } from './identity';
import { getCity } from './location';
import { saveVideo, savePin, getVideo, deleteVideo, saveRemoteProfile, saveFollow, removeFollow } from './metadata';
import { saveRemoteCommunity, joinCommunity, pinToCommunity, storeCommunityEvent, storeChannelMessage, storeCommunityPost, deleteCommunity } from './communities';
import { savePost, deletePost, forceDeletePost, pinPost, unpinPost, getPost } from './posts';
import { saveEventListing, saveRSVP, deleteEventListing } from './eventListings';
import { saveIncomingComment, deleteComment } from './comments';
import { saveIncomingListing, deleteListing } from './listings';
import { saveIncomingDM } from './messages';
import { saveIncomingLike } from './likes';
import { saveContentReport, ADMIN_PUBKEY } from './moderation';
import { pushNotification } from './notifications';
import { solvePoW } from './pow';

const LAST_SEEN_KEY = 'authenticulture_last_seen';

export interface SyncStatus {
  connected: boolean;
  relayCount: number;
  lastSync: number | null;
  rateLimited: boolean;
}

type SyncListener = (status: SyncStatus) => void;

const relayConnections = new Map<string, WebSocket>();
const seenIds = new Set<string>();
let listeners: SyncListener[] = [];
let syncStatus: SyncStatus = { connected: false, relayCount: 0, lastSync: null, rateLimited: false };
let subCounter = 0;

function updateStatus(patch: Partial<SyncStatus>): void {
  syncStatus = { ...syncStatus, ...patch };
  listeners.forEach(fn => fn(syncStatus));
}

export function getSyncStatus(): SyncStatus {
  return syncStatus;
}

export function addSyncStatusListener(fn: SyncListener): () => void {
  listeners.push(fn);
  return () => { listeners = listeners.filter(l => l !== fn); };
}

export function initSync(relayUrls: string[]): void {
  relayUrls.forEach(url => connectRelay(url));
}

export async function publishEvent(type: EventType, payload: EventPayload): Promise<AuthEvent> {
  const identity = loadOrCreateIdentity();
  const location = getCity();
  const payloadWithCity = location
    ? { ...payload, city: location.city, country: location.country }
    : payload;
  const event = await createEvent(type, payloadWithCity as EventPayload, identity.publicKey, identity.privateKey);
  const pow = await solvePoW(event.id);
  const eventWithPow = { ...event, pow };
  seenIds.add(event.id);
  mergeEvent(event);
  if (relayConnections.size > 0) {
    const msg = JSON.stringify(['PUBLISH', eventWithPow]);
    relayConnections.forEach(ws => {
      if (ws.readyState === WebSocket.OPEN) ws.send(msg);
    });
  }
  return event;
}

export function subscribeCity(city: string): void {
  relayConnections.forEach(ws => {
    if (ws.readyState !== WebSocket.OPEN) return;
    const sid = `sub_local_${++subCounter}`;
    const since = Number(localStorage.getItem(LAST_SEEN_KEY) || '0');
    ws.send(JSON.stringify(['SUBSCRIBE', sid, { city, since }]));
  });
}

function connectRelay(url: string, backoff = 1000): void {
  if (relayConnections.has(url)) return;
  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch {
    setTimeout(() => connectRelay(url, Math.min(backoff * 2, 30000)), backoff);
    return;
  }
  relayConnections.set(url, ws);

  ws.onopen = () => {
    const since = Number(localStorage.getItem(LAST_SEEN_KEY) || '0');
    ws.send(JSON.stringify(['SUBSCRIBE', `sub_${++subCounter}`, { since }]));
    const identity = loadOrCreateIdentity();
    ws.send(JSON.stringify(['SUBSCRIBE', `sub_dm_${++subCounter}`, { types: ['dm'], toUserId: identity.userId, since }]));
    updateStatus({ connected: true, relayCount: relayConnections.size });
  };

  ws.onmessage = (e) => {
    try {
      const msg = JSON.parse(e.data);
      if (!Array.isArray(msg)) return;
      if (msg[0] === 'EVENT') handleIncomingEvent(msg[2] as AuthEvent).catch(() => {});
      if (msg[0] === 'EOSE') {
        const now = Date.now();
        localStorage.setItem(LAST_SEEN_KEY, String(now));
        updateStatus({ lastSync: now });
      }
      if (msg[0] === 'OK' && msg[2] === false && msg[3] === 'rate-limited') {
        updateStatus({ rateLimited: true });
      }
      if (msg[0] === 'TAKEDOWN' && typeof msg[1] === 'string') {
        // Verify relay's broadcast carries a valid admin sig before deleting (C-2)
        const [, eventId, sig, timestamp] = msg;
        if (!sig || typeof timestamp !== 'number') return;
        if (!verifySignature(`takedown:${eventId}:${timestamp}`, sig, ADMIN_PUBKEY)) return;
        if (Math.abs(Date.now() - timestamp) > 5 * 60 * 1000) return;
        const removedPost = getPost(eventId);
        if (removedPost && removedPost.pubkey === loadOrCreateIdentity().publicKey) {
          pushNotification({
            id: `takedown_${eventId}`,
            type: 'takedown',
            text: 'One of your posts was removed by a moderator.',
            postId: eventId,
            createdAt: Date.now(),
          });
        }
        forceDeletePost(eventId);
      }
    } catch {}
  };

  ws.onclose = () => {
    relayConnections.delete(url);
    updateStatus({ connected: relayConnections.size > 0, relayCount: relayConnections.size });
    setTimeout(() => connectRelay(url, Math.min(backoff * 2, 30000)), backoff);
  };
}

async function handleIncomingEvent(event: AuthEvent): Promise<void> {
  if (seenIds.has(event.id)) return;
  const valid = await verifyEvent(event);
  if (!valid) return;
  seenIds.add(event.id);
  // Cap seenIds to prevent unbounded memory growth (L-4)
  if (seenIds.size > 50000) {
    const iter = seenIds.values();
    for (let i = 0; i < 10000; i++) seenIds.delete(iter.next().value);
  }
  mergeEvent(event);
}

function mergeEvent(event: AuthEvent): void {
  const p = event.payload as any;
  switch (event.type) {
    case 'video':
      if (!getVideo(p.hash)) saveVideo(p);
      break;
    case 'delete_video':
      deleteVideo(p.hash, p.userId, event.pubkey === ADMIN_PUBKEY);
      break;
    case 'pin':
      savePin(p);
      break;
    case 'profile':
      saveRemoteProfile(p);
      break;
    case 'community':
      saveRemoteCommunity(p);
      break;
    case 'delete_community':
      deleteCommunity(p.communityId, p.userId);
      break;
    case 'community_member':
      joinCommunity(p.communityId, p.userId, p.username);
      break;
    case 'community_pin':
      pinToCommunity(p.communityId, p.videoHash, p.pinnedBy);
      break;
    case 'follow':
      if (p.followerUserId && p.followeeUserId) saveFollow(p.followerUserId, p.followeeUserId);
      break;
    case 'unfollow':
      if (p.followerUserId && p.followeeUserId) removeFollow(p.followerUserId, p.followeeUserId);
      break;
    case 'community_create':
    case 'community_join':
    case 'community_leave':
    case 'community_lock':
    case 'community_unlock':
    case 'channel_create':
    case 'channel_delete':
    case 'mod_grant':
    case 'mod_revoke':
    case 'community_ban':
    case 'community_unban':
    case 'community_invite':
    case 'community_pin_post':
      storeCommunityEvent(event);
      break;
    case 'channel_message':
      storeChannelMessage({
        id: event.id,
        communityId: p.communityId,
        channelId: p.channelId,
        content: p.content,
        userId: p.userId || event.pubkey,
        pubkey: event.pubkey,
        magnetURI: p.magnetURI,
        replyToId: p.replyToId,
        timestamp: event.timestamp,
      });
      break;
    case 'community_post':
      storeCommunityPost({
        id: event.id,
        communityId: p.communityId,
        content: p.content,
        userId: p.userId || event.pubkey,
        pubkey: event.pubkey,
        magnetURI: p.magnetURI,
        timestamp: event.timestamp,
      });
      break;
    case 'post': {
      savePost({ id: event.id, pubkey: event.pubkey, sig: event.sig, timestamp: event.timestamp, payload: p });
      const myIdentity = loadOrCreateIdentity();
      if (p.replyTo?.pubkey === myIdentity.publicKey && event.pubkey !== myIdentity.publicKey) {
        pushNotification({
          id: `reply_${event.id}`,
          type: 'reply',
          text: 'Someone replied to your post.',
          postId: p.replyTo.postId,
          createdAt: event.timestamp,
        });
      }
      break;
    }
    case 'delete_post':
      if (event.pubkey === ADMIN_PUBKEY) forceDeletePost(p.postId);
      else deletePost(p.postId, p.pubkey);
      break;
    case 'pin_post':
      if (event.pubkey === ADMIN_PUBKEY) pinPost(p.postId);
      break;
    case 'unpin_post':
      if (event.pubkey === ADMIN_PUBKEY) unpinPost(p.postId);
      break;
    case 'event_listing':
      saveEventListing({ id: event.id, pubkey: event.pubkey, sig: event.sig, timestamp: event.timestamp, payload: p });
      break;
    case 'delete_event_listing':
      deleteEventListing(p.listingId, p.pubkey);
      break;
    case 'event_rsvp':
      saveRSVP({ id: event.id, pubkey: event.pubkey, sig: event.sig, timestamp: event.timestamp, payload: p });
      break;
    case 'comment':
      saveIncomingComment({
        id: event.id,
        videoHash: p.videoHash,
        userId: p.userId,
        username: p.username,
        text: p.text,
        createdAt: event.timestamp,
      });
      break;
    case 'delete_comment':
      deleteComment(p.commentId, p.userId);
      break;
    case 'listing':
      saveIncomingListing({
        id: event.id,
        userId: p.userId,
        username: p.username,
        title: p.title,
        description: p.description,
        price: p.price,
        paymentUrl: p.paymentUrl,
        category: p.category,
        createdAt: event.timestamp,
      });
      break;
    case 'delete_listing':
      deleteListing(p.listingId, p.userId);
      break;
    case 'like': {
      saveIncomingLike({ id: event.id, targetId: p.targetId, targetType: p.targetType, userId: p.userId, createdAt: event.timestamp });
      const myIdentity = loadOrCreateIdentity();
      if (p.targetType === 'post' && event.pubkey !== myIdentity.publicKey) {
        const likedPost = getPost(p.targetId);
        if (likedPost && likedPost.pubkey === myIdentity.publicKey) {
          pushNotification({
            id: `like_${event.id}`,
            type: 'like',
            text: 'Someone liked your post.',
            postId: p.targetId,
            createdAt: event.timestamp,
          });
        }
      }
      break;
    }
    case 'content_report':
      saveContentReport({
        id: event.id,
        reporterPubkey: event.pubkey,
        targetId: p.targetId,
        targetType: p.targetType,
        reason: p.reason,
        note: p.note,
        createdAt: event.timestamp,
      });
      break;
    case 'dm': {
      const identity = loadOrCreateIdentity();
      if (p.toUserId === identity.userId) {
        saveIncomingDM({
          id: event.id,
          fromUserId: p.fromUserId,
          fromUsername: p.fromUsername,
          toUserId: p.toUserId,
          ciphertext: p.ciphertext,
          nonce: p.nonce,
          fromBoxPubkey: p.fromBoxPubkey,
          createdAt: event.timestamp,
          myIdentity: identity,
        });
        pushNotification({
          id: `dm_${event.id}`,
          type: 'dm',
          text: `New message from ${p.fromUsername || 'someone'}.`,
          fromUserId: p.fromUserId,
          fromUsername: p.fromUsername,
          createdAt: event.timestamp,
        });
      }
      break;
    }
  }
}

export function _resetSyncState(): void {
  relayConnections.clear();
  seenIds.clear();
  listeners = [];
  syncStatus = { connected: false, relayCount: 0, lastSync: null, rateLimited: false };
  subCounter = 0;
}
